// src/components/Header.tsx

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import logoImage from "@/assets/fca-logo-updated.png";

const leftNavigation = [
  { name: "Home", href: "#home" },
  { name: "About Us", href: "#about" },
  { name: "Gallery", href: "#gallery" },
];

const rightNavigation = [
  { name: "Recognitions", href: "#awards" },  // label changed, anchor kept
  { name: "Pricing", href: "#pricing" },
  { name: "Contact", href: "#contact" },
];

const allNavigation = [...leftNavigation, ...rightNavigation];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    // Always close mobile menu after clicking
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-elegant ${
        isScrolled
          ? "backdrop-blur-md border-b border-border elegant-shadow"
          : "backdrop-blur-sm"
      }`}
      style={{
        // Base color when scrolled (solid) vs when at top (apply opacity)
        // color-mix lets us fade the base color against transparent using --nav-bg-opacity (0..1)
        background: isScrolled
          ? "var(--nav-base-bg, var(--color-background))"
          : "color-mix(in srgb, var(--nav-base-bg, #ffffff) calc(var(--nav-bg-opacity, 0.60) * 100%), transparent)",
        position: "fixed",
      }}
    >
      {/* Pattern layer (uses CSS vars set by Theme Editor) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundColor: "var(--nav-pattern-bg, transparent)",
          backgroundImage: "var(--nav-pattern, none)",
          backgroundSize: "var(--nav-pattern-size, auto)",
          backgroundRepeat: "var(--nav-pattern-repeat, repeat)",
          opacity: "var(--nav-pattern-opacity, 1)",
          // Keep it behind everything
          zIndex: 0,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-16 lg:h-20 relative">
          
          {/* Left Navigation - Hidden on mobile */}
          <nav className="hidden lg:flex absolute left-0 right-1/2 justify-end items-center space-x-4 pr-12">
            {leftNavigation.map((item) => (
              <Button
                key={item.name}
                variant="ghost"
                onClick={() => scrollToSection(item.href)}
                className="text-foreground hover:text-primary transition-elegant font-body font-medium"
              >
                {item.name}
              </Button>
            ))}
          </nav>

          {/* Centered Logo */}
          <div className="flex-shrink-0 z-10">
            <a href="/" className="inline-block">
              <div className="h-20 w-20 lg:h-24 lg:w-24 rounded-full bg-white/90 backdrop-blur-sm border border-border/20 flex items-center justify-center elegant-shadow p-2 hover:scale-105 transition-elegant">
                <img 
                  src={logoImage} 
                  alt="Frosted Cake Art Logo" 
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
          </div>

          {/* Right Navigation - Hidden on mobile */}
          <nav className="hidden lg:flex absolute right-0 left-1/2 justify-start items-center space-x-4 pl-12">
            {rightNavigation.map((item) => (
              <Button
                key={item.name}
                variant="ghost"
                onClick={() => scrollToSection(item.href)}
                className="text-foreground hover:text-primary transition-elegant font-body font-medium"
              >
                {item.name}
              </Button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden absolute right-0">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-foreground"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background/95 backdrop-blur-sm">
                <nav className="flex flex-col gap-4 mt-8">
                  {allNavigation.map((item) => (
                  <Button
                    key={item.name}
                    variant="ghost"
                    onClick={() => scrollToSection(item.href)}
                    className="w-full justify-start px-4 py-3 text-foreground hover:text-primary hover:bg-primary/10 transition-elegant font-body font-medium"
                  >
                    {item.name}
                  </Button>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};